import { AfterViewChecked, Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { Table } from '../../models/table';
import {
  INCOME_STATEMENT,
  BALANCE_SHEET,
  CASH_FLOW_STATEMENT,
  OTHER,
  DOC_TYPE_DISPLAY_NAME_LOOKUP,
  PHYSICAL_DOCUMENT_TYPES,
} from '../../../../../../utils/constants';
import { compareTablesForLabeling } from '../../../../../../utils/table-ordering';

const UNCLASSIFIED = OTHER;

export interface CapturedTableCard {
  table: Table;
  displayName: string;
  pageNumber: number;
  statementType: string;
  isCoreStatement: boolean;
  additionalNotesCount: number;
}

@Component({
    selector: 'app-table-id-summary-panel',
    templateUrl: './table-id-summary-panel.component.html',
    styleUrls: ['./table-id-summary-panel.component.scss'],
    standalone: false
})
export class TableIdSummaryPanelComponent implements OnChanges, AfterViewChecked {
  @Input() tables: Array<Table> = [];
  @Input() selectedTable: Table = null;
  @Input() activePageIndex: number = 0;
  @Input() mergeStepLabel: string = 'Data Review';
  @Output() viewTable = new EventEmitter<Table>();
  @Output() reclassifyTable = new EventEmitter<{ table: Table, newType: string }>();
  @Output() unlinkTable = new EventEmitter<Table>();

  @ViewChild('summaryBody') summaryBody: ElementRef<HTMLElement>;

  coreStatements: CapturedTableCard[] = [];
  additionalTables: CapturedTableCard[] = [];
  reclassifyingCardId: CapturedTableCard = null;
  private needsScrollToBottom = false;
  private isFirstLoad = true;

  readonly CORE_TYPES = PHYSICAL_DOCUMENT_TYPES;
  readonly STATEMENT_TYPE_OPTIONS = [
    { value: INCOME_STATEMENT, label: DOC_TYPE_DISPLAY_NAME_LOOKUP[INCOME_STATEMENT] },
    { value: BALANCE_SHEET, label: DOC_TYPE_DISPLAY_NAME_LOOKUP[BALANCE_SHEET] },
    { value: CASH_FLOW_STATEMENT, label: DOC_TYPE_DISPLAY_NAME_LOOKUP[CASH_FLOW_STATEMENT] },
  ];

  readonly RECLASSIFY_OPTIONS = [
    { value: INCOME_STATEMENT, label: DOC_TYPE_DISPLAY_NAME_LOOKUP[INCOME_STATEMENT] },
    { value: BALANCE_SHEET, label: DOC_TYPE_DISPLAY_NAME_LOOKUP[BALANCE_SHEET] },
    { value: CASH_FLOW_STATEMENT, label: DOC_TYPE_DISPLAY_NAME_LOOKUP[CASH_FLOW_STATEMENT] },
    { value: UNCLASSIFIED, label: 'Unclassified' },
  ];

  ngOnChanges(changes: SimpleChanges) {
    if (changes.hasOwnProperty('tables')) {
      const prevCoreCount = this.coreStatements.length;
      this.buildCards();
    }
    if (changes.hasOwnProperty('activePageIndex') && !changes.activePageIndex.firstChange) {
      if (!changes.hasOwnProperty('selectedTable')) {
        requestAnimationFrame(() => this.scrollToSelectedCard());
      }
    }
  }

  ngAfterViewChecked(): void {
    if (this.needsScrollToBottom && this.summaryBody) {
      // Use requestAnimationFrame to ensure scroll happens after all DOM updates and rendering
      requestAnimationFrame(() => {
        const el = this.summaryBody.nativeElement;
        el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
      });
      this.needsScrollToBottom = false;
    }
  }

  private scrollToSelectedCard(): void {
    if (!this.summaryBody) return;
    const container = this.summaryBody.nativeElement as HTMLElement;
    const targetEl = (container.querySelector('.table-card.selected.active-table') ?? container.querySelector('.table-card.selected')) as HTMLElement;
    if (!targetEl) return;

    const targetScrollTop = targetEl.offsetTop - container.clientHeight / 2 + targetEl.clientHeight / 2;
    container.scrollTo({ top: Math.max(0, targetScrollTop), behavior: 'smooth' });
  }

  buildCards(): void {
    const savedScroll = this.summaryBody?.nativeElement?.scrollTop ?? 0;

    const knownTablesBefore = new Set<Table>([
      ...this.coreStatements.map(c => c.table),
      ...this.additionalTables.map(a => a.table),
    ]);

    this.coreStatements = [];
    this.additionalTables = [];
    this.reclassifyingCardId = null;

    this.tables.forEach(table => {
      if (!table.boundingBox) return;

      const isCoreType = this.CORE_TYPES.includes(table.statementType);

      const card: CapturedTableCard = {
        table,
        displayName: isCoreType ? '' : 'Additional Table',
        pageNumber: table.sourcePageNumber + 1,
        statementType: table.statementType || OTHER,
        isCoreStatement: isCoreType,
        additionalNotesCount: 0,
      };

      if (isCoreType) {
        this.coreStatements.push(card);
      } else {
        this.additionalTables.push(card);
      }
    });

    this.coreStatements.sort((a, b) => compareTablesForLabeling(a.table, b.table));
    this.additionalTables.sort((a, b) => compareTablesForLabeling(a.table, b.table));

    this.additionalTables.forEach((card, index) => {
      card.displayName = index === 0 ? 'Additional Table' : `Additional Table ${index + 1}`;
    });

    const coreTypeCounts: Record<string, number> = {};
    this.coreStatements.forEach(card => {
      const baseLabel = DOC_TYPE_DISPLAY_NAME_LOOKUP[card.statementType] || card.statementType;
      coreTypeCounts[card.statementType] = (coreTypeCounts[card.statementType] || 0) + 1;
      card.displayName = coreTypeCounts[card.statementType] > 1
        ? `${baseLabel} ${coreTypeCounts[card.statementType]}`
        : baseLabel;
    });

    if (this.isFirstLoad) {
      this.isFirstLoad = false;
    } else {
      const hasNewAdditional = this.additionalTables.some(card => !knownTablesBefore.has(card.table));
      if (hasNewAdditional) {
        this.needsScrollToBottom = true;
      } else if (savedScroll > 0) {
        requestAnimationFrame(() => this.summaryBody.nativeElement.scrollTop = savedScroll);
      }
    }
  }

  onViewTable(card: CapturedTableCard): void {
    this.viewTable.emit(card.table);
  }

  onCardClick(card: CapturedTableCard, event: Event): void {
    // Don't navigate if clicking on interactive elements
    const target = event.target as HTMLElement;
    const isInteractiveElement = target.closest('button, select, .classify-group');
    
    if (!isInteractiveElement) {
      this.viewTable.emit(card.table);
    }
  }

  toggleReclassify(card: CapturedTableCard): void {
    this.reclassifyingCardId = this.reclassifyingCardId === card ? null : card;
  }

  cancelReclassify(): void {
    this.reclassifyingCardId = null;
  }

  onReclassifyAs(card: CapturedTableCard, event: Event): void {
    const value = (event.target as HTMLSelectElement).value;
    if (value) {
      this.reclassifyTable.emit({ table: card.table, newType: value });
      this.reclassifyingCardId = null;
    }
  }

  onClassifyAs(card: CapturedTableCard, event: Event): void {
    const value = (event.target as HTMLSelectElement).value;
    if (value) {
      this.reclassifyTable.emit({ table: card.table, newType: value });
    }
  }

  getStatementIcon(statementType: string): string {
    switch (statementType) {
      case INCOME_STATEMENT:
        return 'fas fa-chart-line';
      case BALANCE_SHEET:
        return 'fas fa-scale-balanced';
      case CASH_FLOW_STATEMENT:
        return 'fas fa-money-bill-transfer';
      default:
        return 'fas fa-file-lines';
    }
  }
}
